export const CALENDLY = "https://calendly.com/arvind-quantumdesk/demo_meeting_link";

export const NAV = [
  {
    label: "Product",
    href: "#why-us",
    children: [
      { label: "For D2C stores", desc: "Refunds, replacements and order questions on Shopify & WooCommerce", href: "#d2c" },
      { label: "For B2B teams", desc: "Gmail, Outlook, Slack and Jira in one AI-routed desk", href: "#b2b" },
    ],
  },
  { label: "Industries", href: "#industries" },
  { label: "Customers", href: "#customers" },
  { label: "Pricing", href: "/pricing" },
  { label: "Blog", href: "/blog" },
];

export const HERO_STATS = [
  { value: "3 secs", label: "First response time" },
  { value: "85%", label: "Conversational score" },
  { value: "100%", label: "Agent efficiency" },
];

export const CHAT_SCRIPT = [
  { who: "customer", text: "My shirt is 5 days late. Where is it?" },
  { who: "system", text: "Nova AI · identity verified · order #48213 · Linen shirt, size M" },
  { who: "agent", text: "Sorry about the wait. I’ve checked with the carrier: the parcel was damaged in transit and is held at the hub." },
  { who: "agent", text: "I can send a replacement today or cancel the order and refund you in full. Which would you prefer?" },
  { who: "customer", text: "Cancel and refund, please." },
  { who: "system", text: "Nova AI · order cancelled · refund issued to original card · carrier recall filed" },
  { who: "agent", text: "Done. Your refund is on its way and you’ll get a confirmation email shortly. Sorry again for the trouble." },
];

export const GROWING = [
  {
    num: "01",
    title: "Your first support desk",
    desc: "Take it for free until you are convinced by the proof of concept. Full product, your real queue, no card — you pay only after the POC proves itself.",
    cta: "Start the free POC",
  },
  {
    num: "02",
    title: "Scale your business, not your seats",
    desc: "We don't force you to buy seats. Pay for what you use — Finance, AMs and CSMs step into conversations from Slack without a license.",
    cta: "See how pricing works",
  },
  {
    num: "03",
    title: "Outcome-based pricing is our vision",
    desc: "You pay for completed transactions and conversations — the ones that reach a resolution and the CSAT workflow. Dropped-out conversations cost you nothing.",
    cta: "See per-ticket pricing",
  },
  {
    num: "04",
    title: "Starting your D2C? Leave support to us",
    desc: "From refund to replacement, the AI agent runs your support operations inside Shopify and WooCommerce while you run the store.",
    cta: "See the D2C desk",
  },
];

export const B2B_POINTS = [
  "Gmail and Outlook inboxes land in one queue, scored and routed by the AI agent before anyone reads them.",
  "Shared customer Slack channels become tickets. AMs and CSMs step into escalations and renewals from where they already are.",
  "Finance closes a refund from Slack. No login, no seat, full ticket context.",
  "Bugs go to engineering as Jira issues with the customer thread attached — and the customer is told when it ships.",
];

export const D2C_POINTS = [
  "From refund to replacement, handled. Order lookup, refunds, exchanges and replacements inside WooCommerce and Shopify.",
  "AI included, not bolted on. Resolution, routing and CSAT in the box, on every plan.",
  "TikTok and Telegram DMs in the same inbox. One AI agent across every channel your customers actually use.",
  "Migrate and go live in a day. Import history, connect channels, train the agent.",
];

export const D2C_VOLUMES = [
  { tickets: 200, price: 190, per: 0.95 },
  { tickets: 500, price: 425, per: 0.85 },
  { tickets: 1000, price: 790, per: 0.79 },
];

export const AUDIT_STEPS = [
  { n: "1", title: "Connect your help desk", desc: "Your current help desk, shared inbox or plain email — read-only. Takes about ten minutes." },
  { n: "2", title: "Agents replay 30 days of tickets", desc: "Every conversation is scored on resolution, tone and time to first response." },
  { n: "3", title: "Get your validation report", desc: "Deflection rate, hours saved and cost per ticket, side by side with today. Yours to keep." },
];

export const INDUSTRIES = [
  {
    key: "Education",
    sub: "Schools, universities, ed-tech",
    desc: "Admissions, fee queries, timetable changes and LMS access answered the moment a student or parent asks — in term time and out of it.",
    points: [
      "Enrolment and admissions questions resolved on first contact",
      "Fee, refund and scholarship status pulled from your student system",
      "Sensitive cases handed to a counsellor with the full thread attached",
    ],
    stats: [
      { v: "3 secs", l: "First response, even in results week" },
      { v: "24/7", l: "Coverage across time zones" },
    ],
    img: "/industries/education.webp",
  },
  {
    key: "Travel",
    sub: "Airlines, OTAs, tour operators",
    desc: "Rebookings, baggage, visa questions and refunds answered while the traveller is still at the gate — across every time zone you fly.",
    points: [
      "Disruption rebooking handled end to end from the PNR",
      "Refund and voucher status pulled from your booking engine",
      "Duty-of-care cases escalated with the itinerary attached",
    ],
    stats: [
      { v: "3 secs", l: "First response during disruptions" },
      { v: "70%", l: "Rebookings closed without a human" },
    ],
    img: "/industries/travel.webp",
  },
  {
    key: "Hospitality",
    sub: "Hotels, resorts, short-term rentals",
    desc: "Check-in times, late arrivals, amenity requests and billing questions answered before the guest reaches the front desk.",
    points: [
      "Reservation changes made directly in your PMS",
      "Upsell and early check-in offers handled in the same thread",
      "Complaints routed to the duty manager with sentiment flagged",
    ],
    stats: [
      { v: "92%", l: "Guest CSAT on resolved chats" },
      { v: "24/7", l: "Concierge coverage" },
    ],
    img: "/industries/hospitality.webp",
  },
  {
    key: "Gaming & betting",
    sub: "Operators, studios, platforms",
    desc: "Deposits, withdrawals, bonus terms and KYC answered in seconds — on match day and at 3 a.m. — with responsible-gaming cases routed carefully.",
    points: [
      "Withdrawal and bonus status pulled from the wallet in real time",
      "KYC document issues resolved in-chat",
      "Self-exclusion requests handed to a trained human immediately",
    ],
    stats: [
      { v: "3 secs", l: "First response on match day" },
      { v: "88%", l: "Payment queries resolved by agents" },
    ],
    img: "/industries/gaming.webp",
  },
  {
    key: "Insurance",
    sub: "Insurers, brokers, insurtech",
    desc: "Policy questions, claim status and document requests answered instantly, with regulated decisions handed to an adjuster with the file ready.",
    points: [
      "Claim status and next steps pulled from your core system",
      "Policy documents and certificates issued in-chat",
      "Complex claims routed to adjusters with a pre-filled summary",
    ],
    stats: [
      { v: "60%", l: "Claim-status calls deflected" },
      { v: "100%", l: "Audit trail on every decision" },
    ],
    img: "/industries/insurance.webp",
  },
  {
    key: "B2B",
    sub: "SaaS, platforms, services",
    desc: "Login issues, billing, API errors and feature questions answered from Gmail, Outlook and Slack — with bugs filed to Jira automatically.",
    points: [
      "Shared Slack channels become tickets with full context",
      "Invoice and plan changes closed by Finance from Slack",
      "Bugs filed to Jira and the customer told when it ships",
    ],
    stats: [
      { v: "0", l: "Seats for collaborators" },
      { v: "85%", l: "Tier-1 resolved by agents" },
    ],
    img: "/industries/b2b.webp",
  },
  {
    key: "E-commerce",
    sub: "D2C, marketplaces, subscriptions",
    desc: "Where-is-my-order, returns, exchanges and refunds handled inside Shopify and WooCommerce, across TikTok, Telegram and WhatsApp.",
    points: [
      "Order lookup and tracking answered instantly",
      "Refunds and replacements issued by the agent",
      "Black Friday volume absorbed, not queued",
    ],
    stats: [
      { v: "$0.79", l: "Per prepaid resolution" },
      { v: "1 day", l: "To migrate and go live" },
    ],
    img: "/industries/ecommerce.webp",
  },
];

export const LOGOS = {
  Shopify: "/logos/shopify.svg",
  Telegram: "/logos/telegram.svg",
  Slack: "/logos/slack.svg",
  WhatsApp: "/logos/whatsapp.svg",
  TikTok: "/logos/tiktok.svg",
  WooCommerce: "/logos/woocommerce.svg",
  HubSpot: "/logos/hubspot.svg",
  Jira: "/logos/jira.svg",
  Gmail: "/logos/gmail.svg",
  Outlook: "/logos/outlook.svg",
  "Gmail & Outlook": "/logos/gmail.svg",
};

export const CONNECTORS = [
  { logo: "/logos/shopify.svg", name: "Shopify", desc: "D2C commerce" },
  { logo: "/logos/telegram.svg", name: "Telegram", desc: "D2C messaging channel" },
  { logo: "/logo.png", name: "Conversational score", desc: "Quantum Desk native" },
  { logo: "/logo.png", name: "CSAT agent", desc: "Quantum Desk native" },
  { logo: "/logos/slack.svg", name: "Slack", desc: "B2B collaborate without seats" },
  { logo: "/logos/whatsapp.svg", name: "WhatsApp", desc: "Messaging channel" },
  { logo: "/logos/tiktok.svg", name: "TikTok", desc: "D2C social channel" },
  { logo: "/logos/woocommerce.svg", name: "WooCommerce", desc: "D2C commerce" },
  { logo: "/logos/hubspot.svg", name: "HubSpot", desc: "CRM" },
  { logo: "/logos/jira.svg", name: "Jira", desc: "B2B engineering escalations" },
  { logo: "/logos/gmail.svg", logo2: "/logos/outlook.svg", name: "Gmail & Outlook", desc: "B2B email inboxes" },
  { code: "+", name: "Custom integrations", desc: "REST, webhooks and anything with an API" },
];

export const TESTIMONIALS = [
  { quote: "Completely AI-native. Looking forward to them introducing AI Studio.", role: "Chief Operating Officer", industry: "Gaming industry", region: "Liberia", praised: ["AI-native from day one", "Roadmap transparency"] },
  { quote: "Finance closes refunds from Slack now. Nobody on my team paid for a seat they only used to watch.", role: "Head of Customer Support", industry: "B2B SaaS", region: "Netherlands", praised: ["Zero-seat collaboration", "Slack hand-offs"] },
  { quote: "We migrated on a Tuesday and were live by Wednesday. Black Friday felt like a normal week.", role: "Founder", industry: "D2C e-commerce", region: "India", praised: ["One-day migration", "Peak-volume absorption"] },
  { quote: "Results week used to break us. This year first response stayed at three seconds all week.", role: "Director of Student Services", industry: "Higher education", region: "United Kingdom", praised: ["Time to first response", "Counsellor hand-off"] },
  { quote: "The free replay of our last 30 days told us exactly what the agents would have resolved. We signed after the report.", role: "VP Operations", industry: "Travel", region: "UAE", praised: ["Free validation report", "Outcome-based pricing"] },
];

export const HYBRID_BENEFITS = [
  { title: "Free tier to start", desc: "28-day proof of concept with the full product, 2 seats and AI agents. $0, no card." },
  { title: "Pay post-POC", desc: "Your first invoice is raised only after the proof of concept hits the targets you set with us." },
  { title: "Resolution disputes", desc: "Dispute any AI resolution within 30 days. If it doesn't hold up, it's credited back — no questions." },
  { title: "Nobody buys a seat to watch", desc: "Finance, AMs and CSMs step into conversations from Slack. Seats are for the people who work the queue." },
];

export const PLANS = [
  { key: "starter", name: "Starter", tag: "Small teams formalizing their support", price: 25, unit: "/user monthly", maxSeats: 5, cta: "Select plan", features: ["2 AI agents", "Up to 5 seats", "1,000 AI credits / yr", "Unified inbox with all channels", "Shopify & Slack integration"] },
  { key: "growth", name: "Growth", tag: "Scaling teams that live in the inbox", price: 45, unit: "/user monthly", maxSeats: 10, best: true, cta: "Select plan", features: ["4 AI agents", "Up to 10 seats", "2,500 AI credits / yr", "Everything in Starter", "Shopify & Slack agents", "Workflows & SLAs", "Prompt-based analytics"] },
  { key: "pro", name: "Pro", tag: "High-volume support organizations", price: 65, unit: "/user monthly", maxSeats: 20, cta: "Select plan", features: ["Unlimited AI agents", "Up to 20 seats", "5,000 AI credits / yr", "Everything in Growth", "CSM & priority support", "SLA agreement", "SSO & SAML"] },
];

export const PIPELINE = [
  { n: "01", title: "HANDLE", sub: "Every channel, every hour", desc: "WhatsApp, Telegram, Slack, TikTok, email and chat land in one queue. Agents pick up in 3 seconds, at 3 a.m. and on Black Friday.", status: "Volume absorbed, not queued" },
  { n: "02", title: "RESOLVE", sub: "Act, don't just answer", desc: "Agents look up the order in Shopify, update the record in HubSpot, issue the refund and confirm — then score the conversation and ask for CSAT.", status: "85% resolved without a human" },
  { n: "03", title: "HAND OFF", sub: "To a human, with the homework done", desc: "When a case needs judgement, the agent routes it to the right person with a summary, the customer's history and a suggested reply already drafted.", status: "Zero cold hand-offs" },
];

export const MARQUEE = ["Gmail", "Outlook", "Slack", "Jira", "WooCommerce", "Shopify", "TikTok", "Telegram", "WhatsApp", "HubSpot"];
